import { Core } from "@faire/design-tokens";
import { Weight } from "@faire/web--source/slate/Font/utils";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackCategoriesSideNavigationClick } from "@faire/web-api--source/events/categories/click/sideNavigation";
import React from "react";
import styled, { css } from "styled-components";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ILeftNavLink } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/LeftNav/__internal__/ILeftNavLink";

interface SidebarLinkProps {
  option: ILeftNavLink;
  selectedIdentifier?: string;
  dataTestId?: string;
}

/**
 * Determines whether a descendent (including itself) of given {@param leftNavLink}
 * has the same identifier as {@code props.selectedIdentifier}
 */
const containsLeftNavLinkWithMatchingIdentifier = (
  leftNavLink: ILeftNavLink,
  selectedIdentifier: string | undefined
): boolean => {
  return (
    leftNavLink.identifier === selectedIdentifier ||
    leftNavLink.childNavLinks.some((childLink) =>
      containsLeftNavLinkWithMatchingIdentifier(childLink, selectedIdentifier)
    )
  );
};

export const SidebarLink = ({
  dataTestId,
  option,
  selectedIdentifier,
}: SidebarLinkProps) => {
  const childNavLinks: ILeftNavLink[] = option.childNavLinks;
  const isSelected = selectedIdentifier === option.identifier;

  return (
    <Column align="flex-start" justify="flex-start" data-test-id={dataTestId}>
      <StyledLink
        data-test-id="left-nav-link"
        to={option.to}
        aria-current={isSelected ? "page" : undefined}
        onClick={() =>
          trackCategoriesSideNavigationClick(option.identifier ?? "")
        }
      >
        <Text
          variant={isSelected ? "paragraphSansMedium" : "paragraphSansRegular"}
          $isSelected={isSelected}
        >
          {option.name ?? ""}
        </Text>
      </StyledLink>
      {childNavLinks?.length ? (
        <SubCategoriesWrapper>
          {childNavLinks.map((link) => {
            return (
              <SubCategoryListItemWrapper key={link.to}>
                <SubCategoryListItem key={link.to}>
                  <SidebarLink
                    option={link}
                    selectedIdentifier={selectedIdentifier}
                  />
                </SubCategoryListItem>
              </SubCategoryListItemWrapper>
            );
          })}
        </SubCategoriesWrapper>
      ) : null}
    </Column>
  );
};

const SubCategoriesWrapper = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  padding-left: 15px;
`;

const SubCategoryListItemWrapper = styled(Row).attrs({ align: "center" })``;

const SubCategoryListItem = styled.li`
  width: 100%;
`;

const StyledLink = styled(Link)`
  width: 100%;
  height: fit-content;
  min-height: 32px;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-items: flex-start;
`;

const Text = styled(Typography)<{ $isSelected: boolean }>`
  &&& {
    padding-bottom: 2px;
    color: ${Core.text.default};

    ${({ $isSelected }) =>
      $isSelected
        ? css`
            border-bottom: 1px solid ${Core.border.default};
            color: ${Core.text.default};
          `
        : css`
            border-bottom: 1px solid transparent;
          `};

    &:hover,
    &:focus {
      border-bottom: 1px solid ${Core.border.hover};
      font-weight: ${Weight.medium};
      color: ${Core.text.enabled};
    }
  }
`;
